import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import {
  ShiftAttachmentsService,
  UploadedAttachment,
} from '@wilson/api/gateway';
import {
  ListRowOfAttachmentWithMultipleShiftIds,
  ShiftAttachment,
  ShiftAttachmentType,
  ShiftState,
} from '@wilson/interfaces';
import { InvoiceAttachment } from '@wilson/invoicing/interfaces';
import { FilePreviewComponent } from '@wilson/non-domain-specific/stays-form-and-drawer';
import { ShiftDrawerComponent } from '@wilson/payroll/shared';
import { ShiftsService } from '@wilson/shifts';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom } from 'rxjs';
import { BytesToMbPipe } from '../../pipes/bytes-to-mb.pipe';
import { InvoiceAttachmentsHelperService } from '../../services/invoice-attachments-helper.service';

import {
  LocalizedDateFormatPipe,
  LocalizedDateTimeFormatPipe,
} from '@wilson/localization';
import { PipesModule } from '@wilson/pipes';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';

@Component({
  selector: 'wilson-invoice-attachments',
  templateUrl: './invoice-attachments.component.html',
  styleUrls: ['./invoice-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe,
    CdkDropList,
    CdkDrag,
    NzTableModule,
    ShiftDrawerComponent,
    NzTagModule,
    NzTooltipModule,
    BytesToMbPipe,
    NzDropdownModule,
    PipesModule,
    LocalizedDateFormatPipe,
    LocalizedDateTimeFormatPipe,
  ],
})
export class InvoiceAttachmentsComponent implements OnChanges {
  private needsPdfRegeneration = false;
  private readonly shiftsService = inject(ShiftsService);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(NzMessageService);
  private readonly nzModalService = inject(NzModalService);
  private readonly shiftAttachmentsService = inject(ShiftAttachmentsService);
  protected readonly invoiceAttachmentsHelperService = inject(
    InvoiceAttachmentsHelperService,
  );

  @Input({ required: true }) set shiftAttachments(
    value: ListRowOfAttachmentWithMultipleShiftIds[],
  ) {
    this.invoiceAttachmentsHelperService.updateShiftAttachments(value);
  }
  @Input({ required: true }) invoiceAttachments: InvoiceAttachment[] = [];
  @Input() areCheckboxesDisabled = false;
  public readonly compact = input(false);
  @Input() disablePreSelection = false;
  @Output() refreshShiftAttachments = new EventEmitter();
  @Output() generatePdf = new EventEmitter();

  protected readonly displayedRows = computed(() => {
    const rows =
      this.invoiceAttachmentsHelperService.rowOfShiftAttachmentsSignal();
    if (!this.compact()) {
      return rows;
    }
    const checkedIds =
      this.invoiceAttachmentsHelperService.setOfCheckedRowIdsSignal();
    return rows.filter((row) => checkedIds.has(row.id));
  });

  protected allSelected = false;
  protected indeterminate = false;
  protected readonly selectedDrawerShiftSignal = signal<{ id: string } | null>(
    null,
  );
  protected isPreSelectionCompleted = false;
  protected readonly isDrawerVisibleSignal = signal(false);
  protected readonly isPreviewVisibleSignal = signal(false);
  protected ShiftAttachmentType = ShiftAttachmentType;

  ngOnChanges(changes: SimpleChanges): void {
    this.needsPdfRegeneration = false;

    if (
      changes['shiftAttachments'] &&
      this.invoiceAttachmentsHelperService.rowOfShiftAttachmentsSignal()
        .length &&
      !this.isPreSelectionCompleted &&
      !this.disablePreSelection
    ) {
      this.preSelectShiftAttachmentReports();
    }

    if (
      (changes['shiftAttachments'] || changes['invoiceAttachments']) &&
      this.invoiceAttachments.length
    ) {
      this.prepareInvoiceAttachments();
    }

    if (this.needsPdfRegeneration) {
      this.needsPdfRegeneration = false;
      this.generatePdf.emit();
    }
  }

  private preSelectShiftAttachmentReports(): void {
    const rowsToSelect = this.invoiceAttachmentsHelperService
      .rowOfShiftAttachmentsSignal()
      .filter((row) => row.attachmentType === ShiftAttachmentType.Report);
    const selectedRowIds: string[] = [];
    for (const row of rowsToSelect) {
      selectedRowIds.push(row.id);
    }
    this.invoiceAttachmentsHelperService.updateSetOfCheckedRowIds(
      new Set(selectedRowIds),
    );
    this.isPreSelectionCompleted = true;
    this.needsPdfRegeneration = true;
  }

  private prepareInvoiceAttachments(): void {
    this.onAllChecked(false);
    this.invoiceAttachmentsHelperService.updateSetOfCheckedRowIds(new Set());

    const clonedRowOfShiftAttachments = [
      ...this.invoiceAttachmentsHelperService.rowOfShiftAttachmentsSignal(),
    ];
    let isAnyAttachmentMissing = false;
    this.invoiceAttachments.forEach((attachment) => {
      const indexOfRow = clonedRowOfShiftAttachments.findIndex(
        (shiftAttachment) =>
          shiftAttachment.shiftDetails.find(
            (shiftDetail) =>
              shiftDetail.attachmentId === attachment.shiftAttachmentId,
          ) !== undefined,
      );

      if (indexOfRow !== -1) {
        if (attachment.isSelected) {
          const rowWithSelectShifAttachment =
            clonedRowOfShiftAttachments[indexOfRow];
          this.onItemChecked(rowWithSelectShifAttachment.id, true);
        }
        moveItemInArray(
          clonedRowOfShiftAttachments,
          indexOfRow,
          attachment.order - 1,
        );
      } else {
        isAnyAttachmentMissing = true;
      }
    });
    if (isAnyAttachmentMissing) {
      this.messageService.warning(
        this.translate.instant(
          'page.invoicing.invoice_attachments.attachments_missing',
        ),
      );
    }
    this.invoiceAttachmentsHelperService.updateShiftAttachments(
      clonedRowOfShiftAttachments,
    );
    this.needsPdfRegeneration = true;
  }

  protected openDrawer(shiftId: string): void {
    this.selectedDrawerShiftSignal.set({ id: shiftId });
    this.isDrawerVisibleSignal.set(true);
  }

  protected closeDrawerAndReloadList(): void {
    this.isDrawerVisibleSignal.set(false);
    this.refreshShiftAttachments.emit();
  }

  protected onItemChecked(id: string, checked: boolean): void {
    this.updateCheckedSet(id, checked);
    this.refreshCheckedStatus();
  }

  protected onAllChecked(checked: boolean): void {
    this.invoiceAttachmentsHelperService
      .rowOfShiftAttachmentsSignal()
      .forEach(({ id }) => this.updateCheckedSet(id, checked));
    this.refreshCheckedStatus();
  }

  private updateCheckedSet(id: string, isChecked: boolean): void {
    const setOfCheckedId =
      this.invoiceAttachmentsHelperService.setOfCheckedRowIdsSignal();
    if (isChecked) {
      setOfCheckedId.add(id);
    } else {
      setOfCheckedId.delete(id);
    }
    this.invoiceAttachmentsHelperService.updateSetOfCheckedRowIds(
      setOfCheckedId,
    );
  }

  private refreshCheckedStatus(): void {
    this.allSelected = this.invoiceAttachmentsHelperService
      .rowOfShiftAttachmentsSignal()
      .every(({ id }) =>
        this.invoiceAttachmentsHelperService.setOfCheckedRowIdsSignal().has(id),
      );
    this.indeterminate =
      this.invoiceAttachmentsHelperService
        .rowOfShiftAttachmentsSignal()
        .some(({ id }) =>
          this.invoiceAttachmentsHelperService
            .setOfCheckedRowIdsSignal()
            .has(id),
        ) && !this.allSelected;
  }

  protected drop(event: CdkDragDrop<string[]>): void {
    const clonedShiftAttachments = cloneDeep(
      this.invoiceAttachmentsHelperService.rowOfShiftAttachmentsSignal(),
    );
    moveItemInArray(
      clonedShiftAttachments,
      event.previousIndex,
      event.currentIndex,
    );
    this.invoiceAttachmentsHelperService.updateShiftAttachments(
      clonedShiftAttachments,
    );
  }

  protected async acceptShift(shiftId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.shiftsService.updateShiftsBulk([
          { id: shiftId, state: ShiftState.AcceptedTimes },
        ]),
      );
      this.messageService.success(this.translate.instant('popup.save.text'));
      this.selectedDrawerShiftSignal.set({ id: shiftId });
    } catch (error) {
      captureException(error);
      this.messageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  protected async previewFile(
    row: ListRowOfAttachmentWithMultipleShiftIds,
  ): Promise<void> {
    this.isPreviewVisibleSignal.set(true);
    const modal = this.nzModalService.create<FilePreviewComponent>({
      nzContent: FilePreviewComponent,
      nzFooter: null,
      nzWidth: 750,
    });
    if (modal.componentInstance) {
      modal.componentInstance.isLoading = true;
      try {
        let shiftAttachments: ShiftAttachment[] | UploadedAttachment[] = [];

        if (row.attachmentType === ShiftAttachmentType.Report) {
          shiftAttachments = await firstValueFrom(
            this.shiftAttachmentsService.getShiftReportAttachments(
              row.shiftDetails[0].shiftId,
            ),
          );
        } else {
          shiftAttachments = await firstValueFrom(
            this.shiftAttachmentsService.getShiftAttachments(
              row.shiftDetails[0].shiftId,
            ),
          );
        }

        const shiftAttachment = shiftAttachments.find((shiftAttachment) =>
          row.shiftDetails.find(
            (shiftDetail) => shiftDetail.attachmentId === shiftAttachment.id,
          ),
        );
        if (shiftAttachment) {
          modal.componentInstance.url = shiftAttachment.presignedUrl;
          modal.componentInstance.fileName = shiftAttachment.fileName;
          modal.componentInstance.showDownloadButton = true;
          modal.componentInstance.isLoading = false;
        } else {
          modal.componentInstance.isLoading = false;
          modal.close();
          this.messageService.error(
            this.translate.instant('general.something_went_wrong'),
          );
        }
      } catch (error) {
        captureException(error);
        modal.close();
        this.messageService.error(
          this.translate.instant('general.something_went_wrong'),
        );
      }
    }
    await firstValueFrom(modal.afterClose);
    this.isPreviewVisibleSignal.set(false);
  }
}
